import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  DNSZones,
  CreateDNSZones,
  DNSRecordTemp,
  DNSDepartmentTemp,
  DNSRecord,
} from '../../shared/models/dns.models';

@Injectable({
  providedIn: 'root',
})
export class DNSService {
  private http = inject(HttpClient);

  getAllZones({
    page,
    itemsPerPage,
    search,
  }: {
    page: number;
    itemsPerPage: number;
    search?: string;
  }): Observable<[DNSZones[], number]> {
    const params = {
      page,
      itemsPerPage,
      ...(search ? { search } : {}),
    };

    return this.http.get<[DNSZones[], number]>('/dns/zones', {
      params,
    });
  }

  getZones(domain: string): Observable<DNSZones> {
    return this.http.get<DNSZones>(`/dns/zones/${domain}`);
  }

  createDNSZone(newDnsZone: CreateDNSZones): Observable<void> {
    return this.http.post<void>('/dns/zones', newDnsZone);
  }

  deleteDNSZone(zoneId: number): Observable<boolean> {
    return this.http.delete<boolean>('/dns/zones', {
      body: {
        zoneId,
      },
    });
  }

  updateDNSZoneVisibility(zoneId: number, adminOnly: boolean): Observable<void> {
    return this.http.patch<void>(`/dns/zones/${zoneId}`, { adminOnly });
  }

  getDNSZoneRecords(zoneId: number): Observable<DNSRecordTemp[]> {
    return this.http.get<DNSRecordTemp[]>(`/dns/zones/${zoneId}/records`);
  }

  createDNSRecord(id: number, record: DNSRecord): Observable<boolean> {
    return this.http.patch<boolean>(`/dns/zones/${id}/records`, {
      create: [record],
      delete: [],
    });
  }

  deleteDNSRecord(id: number, recordId: number): Observable<boolean> {
    return this.http.patch<boolean>(`/dns/zones/${id}/records`, {
      create: [],
      delete: [recordId],
    });
  }

  searchZones(search: string): Observable<DNSZones[]> {
    return this.http.post<DNSZones[]>('/dns/zones/search', {
      search,
    });
  }

  getDepartments(): Observable<DNSDepartmentTemp[]> {
    return this.http.get<DNSDepartmentTemp[]>('/dns/departments');
  }

  demoteDepartment(departmentId: string): Observable<void> {
    return this.http.delete<void>(`/dns/departments/${departmentId}`);
  }

  promoteDepartment(departmentId: string): Observable<void> {
    return this.http.post<void>('/dns/departments', { id: departmentId });
  }

  syncDNS({
    domain,
    server,
    step,
  }: {
    domain?: string;
    server?: string;
    step?: number;
  }): Observable<void> {
    return this.http.patch<void>('/dns/zones/worker/sync', { domain, server, step });
  }

  activateDNSZone(zoneId: number): Observable<void> {
    return this.http.patch<void>(`/dns/zones/${zoneId}/activate`, {});
  }
}
